import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { ComponentProps, useCallback, useId, useMemo } from 'react'
import { Legend } from 'recharts'

import {
    GranularityType,
    IntervalDataEntry,
    IntervalHighlightsState,
    IntervalReportSettings,
} from '@/hooks/useApi/useActiveManagementApi/types'
import { ActiveManagementFiltersValues } from '@/utils/constants'
import { formatValueWithUnit } from '@/utils/formatValueWithUnit'
import { shortenNumber } from '@/utils/shortenNumber'
import { MultiChart } from '@organisms'
import { Payload as LegendPayload } from 'recharts/types/component/DefaultLegendContent'
import { useActiveManagementMetersQuery } from '../../hooks/useActiveManagementMetersQuery'
import { useActiveManagementSubmetersQuery } from '../../hooks/useActiveManagementSubmetersQuery'
import { getEntryId } from '../../utils/getEntryId'
import { detectDataGrouping } from './detectDataGrouping'
import { HighlightDotsOverlay } from './HighlightDotsOverlay'
import { IntervalDataChartMiniMap } from './IntervalDataChartMiniMap'
import { useBrushProps } from './useBrushProps'
import { useIntervalTooltip } from './useIntervalTooltip'
import { useIntervalXAxisFormatter } from './useIntervalXAxisFormatter'

dayjs.extend(utc)

type IntervalDataChartProps = {
    data: IntervalDataEntry[]
    reportSettings: IntervalReportSettings
    activeFilters: ActiveManagementFiltersValues
    granularity?: GranularityType
    _isFetched?: boolean
    onBrushManualChange?: () => void
    highlights?: IntervalHighlightsState
    showHighlights?: boolean
}

export const IntervalDataChart: React.FC<IntervalDataChartProps> = ({
    data,
    reportSettings,
    activeFilters,
    granularity,
    onBrushManualChange,
    highlights,
    showHighlights = false,
}) => {
    const chartId = useId()
    const { data: meters } = useActiveManagementMetersQuery({ activeFilters })
    const { data: subMeters } = useActiveManagementSubmetersQuery({
        activeFilters,
    })

    const meterMap = useMemo(() => {
        const metersDisplay =
            reportSettings.display === 'subMeters' ? subMeters : meters
        if (!metersDisplay) return new Map<number, string>()
        return new Map(
            metersDisplay.map((meter) => [meter.meterId, meter.meterName])
        )
    }, [meters, subMeters, reportSettings.display])

    // Detect data grouping from data structure (not from settings)
    const dataGrouping = useMemo(
        () => detectDataGrouping(data, reportSettings.display),

        [data, reportSettings.display]
    )

    const processedData = useMemo(() => {
        const { dataType } = reportSettings

        // Extract value based on dataType
        const getValue = (entry: IntervalDataEntry): number | undefined => {
            switch (dataType) {
                case 'usage':
                    return entry.usage
                case 'demandAverage':
                    return entry.demandAverage
                case 'demandMax':
                    return entry.demandMax
                default:
                    return entry.usage
            }
        }

        if (dataGrouping === 'aggregate') {
            // Aggregate mode: group by timestampLocal, sum values
            const timestampMap = new Map<string, number>()

            for (const entry of data) {
                const value = getValue(entry)
                if (value !== undefined && entry.timestampLocal) {
                    // Group by full timestampLocal string
                    const timestamp = entry.timestampLocal
                    const existing = timestampMap.get(timestamp) || 0
                    timestampMap.set(timestamp, existing + value)
                }
            }

            return Array.from(timestampMap.entries())
                .map(([timestamp, value]) => ({
                    date: timestamp,
                    value,
                }))
                .sort((a, b) => {
                    // Sort by timestamp comparison
                    const timestampA = dayjs(a.date)
                    const timestampB = dayjs(b.date)
                    return timestampA.isBefore(timestampB)
                        ? -1
                        : timestampA.isAfter(timestampB)
                          ? 1
                          : 0
                }) as (Record<string, unknown> & { date: string })[]
        } else {
            const allMeterIds = new Set<number>()
            for (const entry of data) {
                const id = getEntryId(entry, reportSettings.display)
                if (id !== undefined) {
                    allMeterIds.add(id)
                }
            }

            const trendMap = new Map<
                string,
                Record<string, unknown> & { date: string }
            >()

            for (const entry of data) {
                const value = getValue(entry)
                const id = getEntryId(entry, reportSettings.display)
                if (value === undefined || !id || !entry.timestampLocal) {
                    continue
                }

                // Group by full timestampLocal string
                const timestamp = entry.timestampLocal
                const meterIdKey = id.toString()

                if (!trendMap.has(timestamp)) {
                    // Initialize entry with all meterIds (undefined for missing values)
                    const timestampEntry: Record<string, unknown> & {
                        date: string
                    } = {
                        date: timestamp,
                    }
                    for (const meterId of allMeterIds) {
                        timestampEntry[meterId.toString()] = undefined
                    }
                    timestampEntry[meterIdKey] = value
                    trendMap.set(timestamp, timestampEntry)
                } else {
                    const existingEntry = trendMap.get(timestamp)
                    if (existingEntry) {
                        // Set the value for this meterId (if multiple entries exist for same timestamp/meter, last one wins)
                        existingEntry[meterIdKey] = value
                    }
                }
            }

            return Array.from(trendMap.values()).sort((a, b) => {
                // Sort by timestamp comparison
                const timestampA = dayjs(a.date)
                const timestampB = dayjs(b.date)
                return timestampA.isBefore(timestampB)
                    ? -1
                    : timestampA.isAfter(timestampB)
                      ? 1
                      : 0
            })
        }
    }, [data, reportSettings, dataGrouping])

    const { brushProps, dataWithTimestamps, brushTimestamps, filteredData } =
        useBrushProps(processedData, granularity, onBrushManualChange)

    const passedBrushProps = useMemo(() => {
        // Show brush if we have valid indices, regardless of fetch state
        // This preserves the brush during refetches when timeline dates change
        const hasValidIndices =
            brushProps.startIndex !== undefined &&
            brushProps.endIndex !== undefined
        if (hasValidIndices) {
            return brushProps
        }
        return undefined
    }, [brushProps])

    const unit = useMemo(() => {
        return reportSettings.dataType === 'usage' ? 'kWh' : 'kW'
    }, [reportSettings.dataType])

    const { xAxisTickFormatter: formatDateForXAxis } =
        useIntervalXAxisFormatter(filteredData)

    // Compute x-axis domain from brush selection timestamps
    // This ensures the domain respects the brush even when there's no data in that range
    const xAxisDomain = useMemo((): [
        number | 'dataMin',
        number | 'dataMax',
    ] => {
        const { startTimestamp, endTimestamp } = brushTimestamps
        const start = startTimestamp ?? 'dataMin'
        const end = endTimestamp ?? 'dataMax'
        return [start, end]
    }, [brushTimestamps])

    // Generate explicit x-axis tick values based on domain
    // This ensures ticks are shown even when there are no data points in the interval
    const xAxisTicks = useMemo((): number[] | undefined => {
        const { startTimestamp, endTimestamp } = brushTimestamps
        if (startTimestamp === undefined || endTimestamp === undefined) {
            return undefined
        }

        const TARGET_TICK_COUNT = 6
        const span = endTimestamp - startTimestamp
        if (span <= 0) {
            return [startTimestamp]
        }

        const tickInterval = span / (TARGET_TICK_COUNT - 1)
        const ticks: number[] = []
        for (let i = 0; i < TARGET_TICK_COUNT; i++) {
            ticks.push(startTimestamp + i * tickInterval)
        }

        // Filter ticks to only include those within the domain range
        // This prevents Recharts from extending the domain to accommodate tick labels
        return ticks.filter(
            (tick) => tick >= startTimestamp && tick <= endTimestamp
        )
    }, [brushTimestamps])

    const { tooltipTitleFormatter, tooltipWrapper, renderTooltipItem } =
        useIntervalTooltip({
            data,
            reportSettings,
            activeFilters,
            meterMap,
            unit,
            highlights: showHighlights ? highlights : undefined,
        })

    const highlightDotsOverlay = useMemo(() => {
        if (
            !showHighlights ||
            !highlights?.highlightKeySet ||
            highlights.highlightKeySet.size === 0
        ) {
            return undefined
        }
        const highlightKeySet = highlights.highlightKeySet
        const Overlay = (props: Record<string, unknown>) => (
            <HighlightDotsOverlay
                {...props}
                highlightKeySet={highlightKeySet}
                dataGrouping={dataGrouping}
                chartId={chartId}
            />
        )
        Overlay.displayName = 'HighlightDotsOverlay'
        return Overlay
    }, [showHighlights, highlights?.highlightKeySet, dataGrouping, chartId])

    const renderLegend = useCallback(
        (props: { payload?: LegendPayload[] }) => {
            if (!props.payload || props.payload.length === 0) {
                return null
            }

            if (dataGrouping === 'aggregate') {
                // Aggregate mode: show single "All (unit)" label
                const firstPayload = props.payload[0]

                return (
                    <div className="flex items-start justify-between gap-4 pb-8">
                        <ul className="flex flex-wrap gap-x-4 gap-y-3">
                            <li className="text-label-xsmall flex items-center gap-1">
                                <div
                                    className="h-2 w-2 shrink-0 rounded-full bg-[currentColor]"
                                    style={{ color: firstPayload.color }}
                                />
                                All ({unit})
                            </li>
                        </ul>
                    </div>
                )
            } else {
                // Compare mode: show one label per meterId with meter name
                return (
                    <div className="flex items-start justify-between gap-4 pb-8">
                        <ul className="flex flex-wrap gap-x-4 gap-y-3">
                            {props.payload.map((payload) => {
                                const dataKey = payload.dataKey as string
                                const meterId = parseInt(dataKey)
                                const meterName =
                                    meterMap.get(meterId) || dataKey

                                return (
                                    <li
                                        key={dataKey}
                                        className="text-label-xsmall flex items-center gap-1"
                                    >
                                        <div
                                            className="h-2 w-2 shrink-0 rounded-full bg-[currentColor]"
                                            style={{ color: payload.color }}
                                        />
                                        {meterName} ({unit})
                                    </li>
                                )
                            })}
                        </ul>
                    </div>
                )
            }
        },
        [dataGrouping, meterMap, unit]
    ) as ComponentProps<typeof Legend>['content']

    const formatYAxisTick = useCallback(
        (number: number) => {
            const { value, sign } = shortenNumber(number)
            const label = formatValueWithUnit(
                {
                    value,
                    sign,
                    unit,
                },
                {}
            )
            return label.replace(/ /g, '\u00A0')
        },
        [unit]
    )

    const getLineProps = useMemo(() => {
        if (dataGrouping === 'compare') {
            return (key: string) => {
                const meterId = parseInt(key)
                const meterName = meterMap.get(meterId) || key
                return {
                    name: meterName,
                }
            }
        }
        return undefined
    }, [dataGrouping, meterMap])

    return (
        <div className="flex h-full max-h-full w-full flex-col gap-4 overflow-hidden">
            <div className="min-h-0 flex-1 overflow-hidden">
                <MultiChart
                    showTooltip={true}
                    data={filteredData}
                    lineChartData={filteredData}
                    xKey="timestamp"
                    xAxisDomain={xAxisDomain}
                    xAxisTicks={xAxisTicks}
                    renderLegend={renderLegend}
                    xAxisTickFormatter={formatDateForXAxis}
                    lineYAxisTickFormatter={formatYAxisTick}
                    getLineProps={getLineProps}
                    renderTooltipItem={renderTooltipItem}
                    tooltipTitleFormatter={tooltipTitleFormatter}
                    tooltipWrapper={tooltipWrapper}
                    highlightDotsOverlay={highlightDotsOverlay}
                    allowOverflow={true}
                />
            </div>

            <IntervalDataChartMiniMap
                data={dataWithTimestamps}
                brushProps={passedBrushProps}
                reportSettings={reportSettings}
                highlightPoints={highlights?.highlightPoints ?? []}
                showHighlights={showHighlights}
            />
        </div>
    )
}
